import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Function_Closures() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🔒 JavaScript Closures</h1>

      <p className="tutorial-intro">
        A <strong>closure</strong> is a function that has access to variables
        from its outer function’s scope even after the outer function has
        returned. Closures are commonly used for data encapsulation and
        maintaining state.
      </p>

      {/* 🧠 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            A closure has access to its own scope, outer function scope, and
            global scope.
          </li>
          <li>
            Closures allow functions to “remember” variables from the outer
            scope.
          </li>
          <li>Useful for data privacy and creating private variables.</li>
          <li>
            Closures are often returned from functions or used in callbacks.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Basic Example */}
      <div className="tutorial-section">
        <h3>1️⃣ Basic Closure Example</h3>
        <p>
          A function inside another function “remembers” variables from its
          outer scope.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function outer() {
  const message = "Hello, World!";
  
  function inner() {
    console.log(message);
  }
  
  return inner;
}

const closureFn = outer();
closureFn();`}
          />
          <TryItButton
            code={`function outer(){const message="Hello, World!";function inner(){console.log(message);}return inner;}const closureFn=outer();closureFn();`}
          />
        </div>
        <OutputBox>Output: Hello, World!</OutputBox>
      </div>

      {/* 2️⃣ Counter Example */}
      <div className="tutorial-section">
        <h3>2️⃣ Using Closures for Counters</h3>
        <p>Closures are useful to maintain private state, like a counter.</p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function createCounter() {
  let count = 0;

  return function() {
    count++;
    return count;
  };
}

const counter = createCounter();
console.log(counter()); // 1
console.log(counter()); // 2
console.log(counter()); // 3`}
          />
          <TryItButton
            code={`function createCounter(){let count=0;return function(){count++;return count;}}const counter=createCounter();console.log(counter());console.log(counter());console.log(counter());`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`1
2
3`}
          </pre>
        </OutputBox>
      </div>

      {/* 3️⃣ Data Privacy Example */}
      <div className="tutorial-section">
        <h3>3️⃣ Data Privacy with Closures</h3>
        <p>You can hide variables from the global scope using closures.</p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function secretHolder(secret) {
  return {
    getSecret: function() {
      return secret;
    },
    setSecret: function(newSecret) {
      secret = newSecret;
    }
  };
}

const obj = secretHolder("mySecret");
console.log(obj.getSecret()); // mySecret
obj.setSecret("newSecret");
console.log(obj.getSecret()); // newSecret`}
          />
          <TryItButton
            code={`function secretHolder(secret){return {getSecret:function(){return secret;},setSecret:function(newSecret){secret=newSecret;}}}const obj=secretHolder("mySecret");console.log(obj.getSecret());obj.setSecret("newSecret");console.log(obj.getSecret());`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`mySecret
newSecret`}
          </pre>
        </OutputBox>
      </div>

      {/* 🚀 Next Step */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Step</h2>
        <p>
          Explore <strong>higher-order functions</strong> next — closures are
          often used to create them and manage state effectively.
        </p>
      </div>
    </div>
  );
}
